// shop-frontend/src/pages/ShopMessages.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/shopMessages.css";

import {
  listConversations,
  getConversationMessages,
  sendMessage,
  deleteMessage,
  deleteConversation,
  startConversation,
} from "../utils/messagesApi";
import { toAbsUrl } from "../utils/api";

/* ── helpers ── */
function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/* ── normalizer ── */
function normalizeConvList(data) {
  const list = data?.conversations || data?.data?.conversations || data?.data || data || [];
  return (Array.isArray(list) ? list : []).map((c) => ({
    _id: c._id || c.id,
    otherType: c.otherType || c.participantType || c.other?.type || "user",
    otherId: c.otherId || c.participantId || c.other?._id || "",
    otherName: c.otherName || c.other?.name || c.other?.shopName || c.other?.fullName || "Unknown",
    otherEmail: c.otherEmail || c.other?.email || c.other?.shopEmail || "",
    otherAvatarUrl: c.otherAvatarUrl || c.other?.avatarUrl || c.other?.photo || "",
    lastText: c.lastText || c.lastMessage?.text || c.lastMessageText || "",
    updatedAt: c.updatedAt || c.lastMessageAt || c.lastMessage?.createdAt || c.createdAt || "",
    unreadCount: Number(c.unreadCount || c.unread || 0),
    topic: c.topic || "general",
    productId: c.productId || c.productPreview?.productId || null,
    productSnapshot: c.productSnapshot ||
      (c.productPreview?.title
        ? {
            productId: c.productPreview.productId || c.productId || "",
            title: c.productPreview.title,
            image: c.productPreview.imageUrl || c.productPreview.image || "",
          }
        : null),
  }));
}

/* ══════════════════════════════════════
   Group conversations by otherId so the
   same user only appears ONCE in the sidebar.
   General chat = primary. Product chats = sub-threads.
   ══════════════════════════════════════ */
function groupByUser(convs) {
  const map = new Map(); // otherId → { primary, threads[], totalUnread }

  for (const c of convs) {
    const key = `${c.otherType}:${c.otherId}`;
    if (!map.has(key)) {
      map.set(key, {
        otherId: c.otherId,
        otherType: c.otherType,
        otherName: c.otherName,
        otherEmail: c.otherEmail,
        otherAvatarUrl: c.otherAvatarUrl,
        primary: null, // general conv
        threads: [], // product convs
        totalUnread: 0,
        latestAt: "",
        latestText: "",
      });
    }
    const g = map.get(key);
    g.totalUnread += c.unreadCount || 0;

    if (c.topic === "general" && !c.productSnapshot?.title) {
      g.primary = c;
    } else {
      g.threads.push(c);
    }

    // track latest activity across all threads
    if (!g.latestAt || (c.updatedAt && new Date(c.updatedAt) > new Date(g.latestAt))) {
      g.latestAt = c.updatedAt;
      g.latestText = c.lastText;
    }
  }

  // If no general conv, promote the most recent thread as primary
  for (const g of map.values()) {
    if (!g.primary && g.threads.length) {
      g.threads.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      g.primary = g.threads.shift();
    }
  }

  // sort groups by latest activity
  return [...map.values()]
    .filter((g) => g.primary)
    .sort((a, b) => new Date(b.latestAt) - new Date(a.latestAt));
}

function isDesktopWidth() {
  try { return typeof window !== "undefined" && window.innerWidth > 900; } catch { return false; }
}

function isPageVisible() {
  try { return typeof document === "undefined" || document.visibilityState === "visible"; } catch { return true; }
}

function newest(list) {
  return [...list].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))[0] || null;
}

// The thread to open for a group: the newest one with unread messages, else the newest.
function pickGroupConv(g) {
  const all = [g.primary, ...g.threads].filter(Boolean);
  return newest(all.filter((c) => (c.unreadCount || 0) > 0)) || newest(all);
}

function pickInitialConv(convs) {
  const groups = groupByUser(convs);
  return groups[0] ? String(pickGroupConv(groups[0])?._id || "") : "";
}

/* ══════════════════════════════════════ */
export default function ShopMessages() {
  const nav = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [messages, setMessages] = useState([]);

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [text, setText] = useState("");
  const [pickedFiles, setPickedFiles] = useState([]);

  // expanded user groups (show product sub-threads)
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const handledNavState = useRef(false);
  const bottomRef = useRef(null);

  /* ── derived ── */
  const activeConv = useMemo(
    () => conversations.find((c) => String(c._id) === String(activeId)) || null,
    [conversations, activeId]
  );

  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (filter === "users") list = list.filter((c) => c.otherType === "user");
    if (filter === "shops") list = list.filter((c) => c.otherType === "shop");

    // search by name or email
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (c) =>
          (c.otherName || "").toLowerCase().includes(q) ||
          (c.otherEmail || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [conversations, filter, search]);

  // Group filtered convos by user
  const grouped = useMemo(() => groupByUser(filteredConversations), [filteredConversations]);

  // Groups with an unread product thread open by themselves so new questions show.
  useEffect(() => {
    const withUnread = grouped
      .filter((g) => g.threads.some((t) => (t.unreadCount || 0) > 0))
      .map((g) => `${g.otherType}:${g.otherId}`);
    if (!withUnread.length) return;
    setExpandedGroups((prev) => {
      if (withUnread.every((k) => prev.has(k))) return prev;
      const next = new Set(prev);
      withUnread.forEach((k) => next.add(k));
      return next;
    });
  }, [grouped]);

  /* ── loaders ── */
  function scrollToBottom() {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
  }

  async function loadConversations(opts = {}) {
    const quiet = !!opts.quiet; // background refresh: no "Loading…" flicker
    if (!quiet) { setErr(""); setLoading(true); }
    try {
      const data = await listConversations();
      const norm = normalizeConvList(data);
      setConversations(norm);

      // Never auto-select on a refresh (that cleared unread badges unseen and
      // undid mobile "back"/delete). Only the very first load on a wide screen
      // opens the newest chat.
      if (opts.initial && isDesktopWidth()) {
        setActiveId((prev) => prev || pickInitialConv(norm));
      } else {
        setActiveId((prev) => (prev && !norm.some((x) => String(x._id) === String(prev)) ? "" : prev));
      }
      return norm;
    } catch (e) {
      if (quiet) return conversations;
      setErr(e?.message || "Failed to load conversations.");
      setConversations([]);
      setActiveId("");
      return [];
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  async function loadMessages(conversationId, opts = {}) {
    const quiet = !!opts.quiet;
    if (!conversationId) return;
    if (!quiet) setErr("");
    try {
      const data = await getConversationMessages(conversationId);
      const list = data?.messages || data?.data?.messages || data?.data || data || [];
      const norm = (Array.isArray(list) ? list : []).map((m) => ({
        _id: m._id || m.id,
        senderType: m.senderType || m.fromType || (m.fromShopId ? "shop" : "user"),
        senderEntityId: m.senderEntityId || m.senderId || "",
        text: m.text || m.body || "",
        attachments: m.attachments || m.files || [],
        createdAt: m.createdAt || m.sentAt || "",
        readBy: m.readBy || [],
      }));
      let changed = true;
      setMessages((prev) => {
        const same = prev.length === norm.length && String(prev[prev.length - 1]?._id || "") === String(norm[norm.length - 1]?._id || "")
          && JSON.stringify(prev.map((m) => (m.readBy || []).length)) === JSON.stringify(norm.map((m) => (m.readBy || []).length));
        changed = !same;
        return same ? prev : norm;
      });
      if (!quiet || changed) scrollToBottom();
    } catch (e) {
      if (quiet) return;
      setErr(e?.message || "Failed to load messages.");
      setMessages([]);
    }
  }

  /* ── nav state handler (from Customers page) ── */
  async function handleStartNewConvo(navState, convList) {
    if (navState?.openConversationId) {
      setActiveId(String(navState.openConversationId));
      return;
    }
    if (!navState?.startNewConvo || !navState?.participantId) return;
    const { participantType = "user", participantId } = navState;

    const existing = convList.find(
      (c) =>
        String(c.otherId) === String(participantId) &&
        c.otherType === participantType &&
        c.topic === "general"
    );

    if (existing) {
      setActiveId(String(existing._id));
      return;
    }

    try {
      const result = await startConversation({ participantType, participantId });
      const newConvId = result?._id || result?.conversation?._id || result?.conversationId || "";
      if (newConvId) {
        await loadConversations();
        setActiveId(String(newConvId));
      }
    } catch (e) {
      setErr(e?.message || "Failed to start conversation.");
    }
  }

  /* ── effects ── */
  useEffect(() => {
    (async () => {
      const convList = await loadConversations({ initial: true });
      const navState = { ...(location.state || {}) };
      // Deep link from a notification: /shop/messages?c=<conversationId>
      const qc = new URLSearchParams(location.search || "").get("c");
      if (qc) navState.openConversationId = qc;
      if ((navState?.startNewConvo || navState?.openConversationId) && !handledNavState.current) {
        handledNavState.current = true;
        nav(location.pathname, { replace: true, state: {} });
        await handleStartNewConvo(navState, convList);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A notification link while already on this page: /shop/messages?c=<id>
  useEffect(() => {
    if (!handledNavState.current) return; // first mount is handled above
    const qc = new URLSearchParams(location.search || "").get("c");
    if (!qc) return;
    setActiveId(String(qc));
    nav(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    loadMessages(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // ✅ Poll for new messages every 6 seconds when a conversation is open
  // Skipped while the tab is hidden: each fetch marks the chat read.
  useEffect(() => {
    if (!activeId) return;
    const msgTimer = setInterval(() => {
      if (isPageVisible()) loadMessages(activeId, { quiet: true });
    }, 6000);
    return () => clearInterval(msgTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // ✅ Poll conversation list every 30 seconds to surface new conversations
  useEffect(() => {
    const convTimer = setInterval(() => {
      if (isPageVisible()) loadConversations({ quiet: true });
    }, 30000);
    return () => clearInterval(convTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── actions ── */
  function onPickFiles(e) {
    const picked = Array.from(e.target.files || []);
    setPickedFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  }

  function removeFile(idx) {
    setPickedFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleDeleteMessage(messageId) {
    if (!activeId || !messageId) return;
    try {
      await deleteMessage(activeId, messageId);
      setMessages((prev) => prev.filter((m) => String(m._id) !== String(messageId)));
    } catch (e) {
      setErr(e?.message || "Failed to delete message.");
    }
  }

  async function handleDeleteConversation(e, conversationId) {
    e.stopPropagation();
    if (!conversationId) return;
    try {
      await deleteConversation(conversationId);
      setConversations((prev) => prev.filter((c) => String(c._id) !== String(conversationId)));
      if (String(activeId) === String(conversationId)) {
        setActiveId("");
        setMessages([]);
      }
    } catch (e) {
      setErr(e?.message || "Failed to delete conversation.");
    }
  }

  function toggleGroup(groupKey) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey);
      return next;
    });
  }

  async function onSend() {
    if (!activeId) return;
    const trimmed = String(text || "").trim();
    if (!trimmed && pickedFiles.length === 0) return;

    const optimistic = {
      _id: `tmp-${Date.now()}`,
      senderType: "shop",
      text: trimmed,
      attachments: pickedFiles.map((f) => ({
        name: f.name,
        kind: f.type?.startsWith("image/") ? "image" : "file",
      })),
      createdAt: new Date().toISOString(),
      optimistic: true,
      readBy: [],
    };
    setMessages((prev) => [...prev, optimistic]);
    scrollToBottom();

    setText("");
    const filesToSend = [...pickedFiles];
    setPickedFiles([]);

    try {
      if (filesToSend.length > 0) {
        const form = new FormData();
        form.append("text", trimmed);
        filesToSend.forEach((f) => form.append("files", f));
        await sendMessage(activeId, form);
      } else {
        await sendMessage(activeId, { text: trimmed });
      }
      await loadMessages(activeId);
      await loadConversations();
    } catch (e) {
      setErr(e?.message || "Failed to send message.");
      setMessages((prev) => prev.filter((m) => !String(m._id).startsWith("tmp-")));
    }
  }

  /* ── read receipt helper ── */
  function ReadReceipt({ msg }) {
    if (msg.senderType !== "shop") return null;
    if (msg.optimistic) return <span className="ms-receipt ms-receipt-sending">○</span>;

    const readByOther = (msg.readBy || []).some(
      (r) => String(r.type || "").toLowerCase() !== "shop"
    );
    if (readByOther) {
      return <span className="ms-receipt ms-receipt-read" title="Read">✓✓</span>;
    }
    return <span className="ms-receipt ms-receipt-sent" title="Sent">✓</span>;
  }

  /* ═════════════════════
     RENDER
     ═════════════════════ */
  return (
    <div className={`ms-wrap ${activeId ? "ms-chat-active" : ""}`}>
      {/* ── LEFT PANEL ── */}
      <div className="ms-left">
        <div className="ms-left-top">
          <div className="ms-title">Messages</div>
          {err && !activeId ? (
            <div style={{ margin: "8px 0", padding: "8px 10px", borderRadius: 10, background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.4)", color: "#fca5a5", fontSize: 13 }}>
              {err}
            </div>
          ) : null}

          {/* Search */}
          <div className="ms-search-wrap">
            <span className="ms-search-icon">🔍</span>
            <input
              className="ms-search"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="ms-search-clear" onClick={() => setSearch("")}>✕</button>
            )}
          </div>

          <div className="ms-tabs">
            {["all", "users", "shops"].map((t) => (
              <button
                key={t}
                className={filter === t ? "ms-tab active" : "ms-tab"}
                onClick={() => setFilter(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="ms-conv-list">
          {loading ? (
            <div className="ms-empty">Loading…</div>
          ) : grouped.length === 0 ? (
            <div className="ms-empty">
              {search ? "No conversations match your search." : "No conversations yet."}
            </div>
          ) : (
            grouped.map((g) => {
              const c = g.primary;
              const groupKey = `${g.otherType}:${g.otherId}`;
              const isExpanded = expandedGroups.has(groupKey);
              const hasThreads = g.threads.length > 0;
              const isActive = String(c._id) === String(activeId);
              const anyThreadActive = g.threads.some((t) => String(t._id) === String(activeId));

              const avatarSrc = g.otherAvatarUrl ? toAbsUrl(g.otherAvatarUrl) : "";
              const isUser = g.otherType === "user";

              return (
                <div key={groupKey} className="ms-conv-group">
                  {/* Main conversation row */}
                  <div
                    className={`ms-conv ${isActive || anyThreadActive ? "active" : ""}`}
                    onClick={() => {
                      const target = pickGroupConv(g) || c;
                      setActiveId(String(target._id));
                      if (hasThreads) setExpandedGroups((prev) => (prev.has(groupKey) ? prev : new Set(prev).add(groupKey)));
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <span
                      className="ms-conv-delete"
                      title="Delete conversation"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleDeleteConversation(e, c._id)}
                    >
                      🗑️
                    </span>

                    <div
                      className={`ms-conv-avatar${isUser ? " ms-conv-avatar--link" : ""}`}
                      onClick={
                        isUser
                          ? (e) => {
                              e.stopPropagation();
                              nav(`/shop/customers?userId=${g.otherId}`);
                            }
                          : undefined
                      }
                      title={isUser ? `View ${g.otherName} in Customers` : undefined}
                    >
                      {avatarSrc ? (
                        <img src={avatarSrc} alt={g.otherName || ""} />
                      ) : (
                        <div className="ms-avatar-initials">{initials(g.otherName)}</div>
                      )}
                    </div>

                    <div className="ms-conv-body">
                      <div className="ms-conv-row">
                        <span
                          className={`ms-conv-name${isUser ? " ms-conv-name--link" : ""}`}
                          onClick={
                            isUser
                              ? (e) => {
                                  e.stopPropagation();
                                  nav(`/shop/customers?userId=${g.otherId}`);
                                }
                              : undefined
                          }
                        >
                          {g.otherName}
                        </span>
                        <div className="ms-conv-time">{timeAgo(g.latestAt)}</div>
                      </div>

                      <div className="ms-conv-row2">
                        <div className="ms-conv-preview">
                          {c.productSnapshot?.title ? (
                            <span className="ms-pill ms-pill-product">Product</span>
                          ) : (
                            <span className="ms-pill gray">General</span>
                          )}
                          <span className="ms-preview-text">{g.latestText || "—"}</span>
                        </div>

                        <div className="ms-conv-badges">
                          {hasThreads && (
                            <button
                              className="ms-thread-toggle"
                              title={`${g.threads.length} product thread${g.threads.length > 1 ? "s" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGroup(groupKey);
                              }}
                            >
                              {g.threads.length} 💬 {isExpanded ? "▾" : "▸"}
                            </button>
                          )}
                          {g.totalUnread > 0 && (
                            <div className="ms-unread">{g.totalUnread}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Product sub-threads (expanded) */}
                  {isExpanded &&
                    [c, ...g.threads].map((t) => {
                      const tActive = String(t._id) === String(activeId);
                      return (
                        <div
                          key={t._id}
                          className={`ms-conv ms-conv-sub ${tActive ? "active" : ""}`}
                          onClick={() => setActiveId(String(t._id))}
                          role="button"
                          tabIndex={0}
                        >
                          <span
                            className="ms-conv-delete"
                            title="Delete conversation"
                            role="button"
                            tabIndex={0}
                            onClick={(e) => handleDeleteConversation(e, t._id)}
                          >
                            🗑️
                          </span>
                          <div className="ms-conv-sub-indent" />
                          <div className="ms-conv-body">
                            {t.productSnapshot?.title ? (
                              <div className="ms-conv-product-row">
                                {t.productSnapshot.image && (
                                  <img
                                    className="ms-conv-product-thumb"
                                    src={toAbsUrl(t.productSnapshot.image)}
                                    alt=""
                                  />
                                )}
                                <span className="ms-conv-product-title">
                                  {t.productSnapshot.title}
                                </span>
                                <span className="ms-pill ms-pill-product">Product</span>
                              </div>
                            ) : (
                              <div className="ms-conv-name" style={{ fontSize: 13 }}>
                                General
                              </div>
                            )}
                            <div className="ms-conv-row2">
                              <span className="ms-preview-text">{t.lastText || "—"}</span>
                              {t.unreadCount > 0 && (
                                <div className="ms-unread">{t.unreadCount}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="ms-right">
        {!activeConv ? (
          <div className="ms-right-empty">Select a conversation</div>
        ) : (
          <>
            {/* Header */}
            <div className="ms-chat-header">
              <button
                type="button"
                className="ms-back-btn"
                onClick={() => setActiveId(null)}
                title="Back to conversations"
              >
                ←
              </button>
              {(() => {
                const av = activeConv.otherAvatarUrl
                  ? toAbsUrl(activeConv.otherAvatarUrl)
                  : "";
                const isUser = activeConv.otherType === "user";
                const inner = (
                  <>
                    <div className="ms-chat-avatar">
                      {av ? (
                        <img src={av} alt={activeConv.otherName || ""} />
                      ) : (
                        <div className="ms-chat-avatar-initials">
                          {initials(activeConv.otherName)}
                        </div>
                      )}
                    </div>
                    <div className="ms-chat-header-text">
                      <div className="ms-chat-name">
                        {activeConv.otherName || "Customer"}
                        <span className="ms-chat-type">{activeConv.otherType || ""}</span>
                      </div>
                      <div className="ms-chat-sub">
                        {activeConv.productSnapshot?.title
                          ? `💬 Asking about: ${activeConv.productSnapshot.title}`
                          : "General chat"}
                      </div>
                    </div>
                  </>
                );
                return (
                  <div
                    className={`ms-chat-user-link${isUser ? "" : " static"}`}
                    onClick={
                      isUser
                        ? () =>
                            nav(
                              `/shop/customers?userId=${activeConv.otherId}`
                            )
                        : undefined
                    }
                    title={
                      isUser
                        ? `View ${activeConv.otherName} in Customers`
                        : undefined
                    }
                    style={isUser ? { cursor: "pointer" } : undefined}
                  >
                    {inner}
                  </div>
                );
              })()}
            </div>

            {/* Product card */}
            {activeConv.productSnapshot?.title &&
              (() => {
                const pid = String(
                  activeConv.productSnapshot.productId ||
                    activeConv.productId ||
                    ""
                ).trim();
                return (
                  <div
                    className="ms-product-card"
                    style={{
                      cursor: pid ? "pointer" : "default",
                    }}
                    onClick={() =>
                      pid && nav(`/shop/dashboard/edit-product/${pid}`)
                    }
                    title={pid ? "View product in your shop" : ""}
                  >
                    {activeConv.productSnapshot?.image ? (
                      <img
                        className="ms-product-img"
                        src={toAbsUrl(activeConv.productSnapshot.image)}
                        alt=""
                      />
                    ) : (
                      <div className="ms-product-img placeholder" />
                    )}
                    <div className="ms-product-info">
                      <div className="ms-product-title">
                        {activeConv.productSnapshot.title}
                      </div>
                      <div className="ms-product-mini">
                        {pid
                          ? "Tap to view product in your shop →"
                          : "Product question"}
                      </div>
                    </div>
                  </div>
                );
              })()}

            {err && <div className="ms-empty" style={{ color: "salmon" }}>{err}</div>}

            {/* Messages */}
            <div className="ms-chat-body">
              {messages.length === 0 ? (
                <div className="ms-empty">No messages yet. Say hello! 👋</div>
              ) : (
                messages.map((m) => {
                  const mine = m.senderType === "shop";
                  return (
                    <div
                      key={m._id}
                      className={mine ? "ms-bubble mine" : "ms-bubble"}
                    >
                      {mine && (
                        <button
                          className="ms-delete-btn"
                          title="Delete message"
                          onClick={() => handleDeleteMessage(m._id)}
                        >
                          🗑️
                        </button>
                      )}
                      {m.text && <div className="ms-text">{m.text}</div>}

                      {Array.isArray(m.attachments) &&
                        m.attachments.length > 0 && (
                          <div className="ms-att-wrap">
                            {m.attachments.map((a, idx) => {
                              const url = a.url ? toAbsUrl(a.url) : "";
                              const kind = a.kind || "file";
                              if (kind === "image" && url)
                                return (
                                  <a
                                    key={idx}
                                    className="ms-att"
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <img src={url} alt="" />
                                  </a>
                                );
                              if (kind === "video" && url)
                                return (
                                  <div key={idx} className="ms-att video">
                                    <video controls src={url} />
                                  </div>
                                );
                              return (
                                <a
                                  key={idx}
                                  className="ms-file"
                                  href={url || "#"}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  📎 {a.name || a.filename || "File"}
                                </a>
                              );
                            })}
                          </div>
                        )}

                      <div className="ms-time">
                        {formatTime(m.createdAt)}
                        {mine && <ReadReceipt msg={m} />}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* Composer */}
            <div className="ms-composer">
              <label className="ms-clip" title="Attach files">
                📎
                <input
                  type="file"
                  multiple
                  onChange={onPickFiles}
                  style={{ display: "none" }}
                  accept="image/jpeg,image/png,image/webp,image/gif,image/heic,video/mp4,video/webm,video/quicktime,audio/*,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
                />
              </label>

              <input
                className="ms-input"
                placeholder="Type a message…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) onSend();
                }}
              />

              <button className="ms-send" type="button" onClick={onSend}>
                Send
              </button>
            </div>

            {pickedFiles.length > 0 && (
              <div className="ms-file-preview">
                {pickedFiles.map((f, idx) => (
                  <div key={idx} className="ms-file-chip">
                    📎 {f.name}
                    <button type="button" onClick={() => removeFile(idx)}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
